import { fetch as tauriFetch } from '@tauri-apps/plugin-http';
import { isTauriAppPlatform } from '@/services/environment';
import { MINERVA_OPDS_USER_AGENT } from '@/services/constants';
import { useSettingsStore } from '@/store/settingsStore';
import {
  needsProxy,
  getProxiedURL,
  probeAuth,
  withOriginSuppressed,
} from '@/app/opds/utils/opdsReq';
import { normalizeCustomHeaders } from '@/utils/customHeaders';
import { allowsSelfSignedCertAt, tlsFetch } from '@/utils/tls';
import { trackWidePages, type WidePagesOptions } from '@/utils/spread';
import type { BookFormat } from '@/types/book';
import type { BookDoc, BookMetadata } from '@/libs/document';

export const PSE_SCHEME = 'pse://';

export interface PseStreamData {
  url: string;
  catalogId: string;
  count: number;
  title: string;
  author: string;
}

export const isPseStreamFileName = (name: string): boolean => name.startsWith(PSE_SCHEME);

export const buildPseStreamFileName = (data: PseStreamData): string =>
  PSE_SCHEME + encodeURIComponent(JSON.stringify(data));

export const parsePseStreamFileName = (name: string): PseStreamData =>
  JSON.parse(decodeURIComponent(name.replace(PSE_SCHEME, '')));

const PAGE_NUMBER_PATTERN = /%7BpageNumber%7D|\{pageNumber\}/gi;
const MAX_WIDTH_PATTERN = /%7BmaxWidth%7D|\{maxWidth\}/gi;
const DEFAULT_MAX_WIDTH = '2000';

export const createPseStreamPageLoader = (data: PseStreamData) => {
  const settings = useSettingsStore.getState().settings;
  const catalog = settings.opdsCatalogs?.find((c) => c.id === data.catalogId);
  const username = catalog?.username || '';
  const password = catalog?.password || '';
  const customHeaders = normalizeCustomHeaders(catalog?.customHeaders);
  let authHeaderPromise: Promise<string | null> | null = null;

  return async (pageIndex: number): Promise<Blob> => {
    let url = data.url.replace(PAGE_NUMBER_PATTERN, pageIndex.toString());
    url = url.replace(MAX_WIDTH_PATTERN, DEFAULT_MAX_WIDTH);
    const useProxy = needsProxy(url);
    const allowSelfSignedCert = allowsSelfSignedCertAt(catalog ?? {}, url);

    if (!authHeaderPromise) {
      authHeaderPromise =
        username || password
          ? probeAuth(url, username, password, useProxy, customHeaders, allowSelfSignedCert)
          : Promise.resolve(null);
    }
    const authHeader = await authHeaderPromise;

    const fetchURL = useProxy ? getProxiedURL(url, true, customHeaders) : url;
    const headers: Record<string, string> = withOriginSuppressed({
      'User-Agent': MINERVA_OPDS_USER_AGENT,
      ...(!useProxy ? customHeaders : {}),
      ...(authHeader ? { Authorization: authHeader } : {}),
    });
    const fetch = isTauriAppPlatform() ? tauriFetch : window.fetch;
    const res = await tlsFetch(fetch, fetchURL, { headers }, allowSelfSignedCert);
    if (!res.ok) {
      throw new Error(`Failed to fetch page ${pageIndex}: ${res.statusText}`);
    }
    return res.blob();
  };
};

export const openPseStreamBook = async (
  data: PseStreamData,
  widePages?: WidePagesOptions,
): Promise<{ book: BookDoc; format: BookFormat }> => {
  const loadPage = createPseStreamPageLoader(data);
  const entries = Array.from({ length: data.count }).map((_, i) => ({
    filename: `${i.toString().padStart(4, '0')}.jpg`,
    directory: false,
    size: 0,
  }));
  const loader = {
    entries,
    loadText: async () => null,
    loadBlob: async (name: string) => {
      const i = parseInt(name.split('.')[0] || '0', 10);
      return loadPage(i);
    },
    getSize: () => 0,
    getComment: async () => null,
  };
  const { makeComicBook } = await import('foliate-js/comic-book.js');
  // makeComicBook only consults `file.name` as a fallback title; we override
  // metadata from `data` regardless, so a name-only stand-in is sufficient.
  // Each page streams in on its own request, so it is measured as it arrives.
  const pages = widePages && trackWidePages(loader, widePages.onFound);
  const rawComicBook = await makeComicBook(pages?.loader ?? loader, { name: data.title });
  pages?.attach(rawComicBook.sections, widePages?.known ?? []);
  const book = {
    ...rawComicBook,
    dir: 'auto',
    metadata: {
      ...(rawComicBook.metadata || {}),
      title: data.title,
      author: data.author,
    } as BookMetadata,
  } as BookDoc;
  return { book, format: 'CBZ' };
};
